import { useCallback, useEffect, useRef, useState } from 'react';
import {
  PageEventQueue,
  type PageEventQueueOptions,
} from '../utils/pageEventQueue';

/**
 * 管理页面层消息队列实例，统一封装：
 * 1. 队列创建 / 销毁
 * 2. 暂停 / 继续状态
 * 3. flush / reset / enqueue 等常用操作
 */
export function usePageEventQueue<T>() {
  /** 当前运行中的页面层队列实例。 */
  const queueRef = useRef<PageEventQueue<T> | null>(null);
  /** 页面层消费是否处于暂停态，用于按钮展示。 */
  const [isPaused, setIsPaused] = useState(false);

  /** 组件卸载时销毁队列，防止旧任务残留定时器。 */
  useEffect(() => {
    return () => {
      queueRef.current?.destroy();
      queueRef.current = null;
    };
  }, []);

  /** 创建新队列；用于每次开始新任务时重建独立实例。 */
  const createQueue = useCallback((options: PageEventQueueOptions<T>) => {
    queueRef.current?.destroy();
    queueRef.current = new PageEventQueue<T>(options);
    setIsPaused(false);
  }, []);

  /** 入队；返回 false 表示当前还没有可用队列。 */
  const enqueue = useCallback((item: T) => {
    if (!queueRef.current) return false;
    queueRef.current.enqueue(item);
    return true;
  }, []);

  /** 等待页面层所有已入队事件消费完毕。 */
  const flush = useCallback(() => {
    return queueRef.current?.flush() || Promise.resolve();
  }, []);

  /** 重置队列内部状态，但保留实例本身，便于同一轮任务中复位。 */
  const reset = useCallback(() => {
    queueRef.current?.reset();
    setIsPaused(false);
  }, []);

  /** 销毁当前实例并同步清空暂停态。 */
  const destroy = useCallback(() => {
    queueRef.current?.destroy();
    queueRef.current = null;
    setIsPaused(false);
  }, []);

  /** 运行页操作：在暂停与继续之间切换。 */
  const togglePause = useCallback(() => {
    if (!queueRef.current) return;
    setIsPaused(prev => {
      const nextPaused = !prev;
      if (nextPaused) {
        queueRef.current?.pause();
      } else {
        queueRef.current?.resume();
      }
      return nextPaused;
    });
  }, []);

  /** 由后端 pause 事件触发的强制暂停。 */
  const pause = useCallback(() => {
    if (!queueRef.current) return;
    queueRef.current.pause();
    setIsPaused(true);
  }, []);

  /** pause 问题处理完成后自动恢复。 */
  const resume = useCallback(() => {
    if (!queueRef.current) return;
    queueRef.current.resume();
    setIsPaused(false);
  }, []);

  return {
    isPaused,
    createQueue,
    enqueue,
    flush,
    reset,
    destroy,
    togglePause,
    pause,
    resume,
  };
}
